import { useCallback, useState } from 'react';

import { isEmpty } from 'lodash-es';
import queryString from 'query-string';
import { useIntl } from 'react-intl';

import { accessTokenStorage } from '@/utils/AccessTokenStorage';

import useAuthentication from './useAuthentication';
import useError, {
  type ErrorResponse as CommonErrorResponse,
} from './useError';

type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
type RequestHeaders = { [key: string]: string };
type RequestBody = { [key: string]: unknown };
type CustomHeaders = {
  'X-Token-Type'?:
    'access_token' | 'email_registration_token' | 'password_change_token';
};

interface SendRequestOptions {
  body?: RequestBody;
  headers?: RequestHeaders & CustomHeaders;
}

interface SendRequestReturnType<R> {
  data: R;
  status: Response['status'];
}

interface Result<R, E> {
  fetch: (params?: SendRequestOptions) => Promise<SendRequestReturnType<R>>;
  reset: () => void;
  isFetching: boolean;
  error: E | null;
  data: R | null;
  status: number | null;
  headers: Headers | null;
}

const buildRequestHeaders = (
  headers: RequestHeaders,
  accessToken: string | null,
): RequestHeaders & CustomHeaders => {
  const commonHeaders = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    return {
      'X-Token-Type': 'access_token',
      Authorization: `Bearer ${accessToken}`,
      ...commonHeaders,
      ...headers,
    };
  } else {
    return {
      ...commonHeaders,
      ...headers,
    };
  }
};

const buildRequestPayload = (
  method: HTTPMethod,
  url: string,
  params: unknown,
  headers?: RequestHeaders,
): Parameters<typeof fetch> => {
  if (method === 'GET' || method === 'DELETE') {
    const requestUrl = isEmpty(params)
      ? url
      : `${url}?${queryString.stringify(
          params as Parameters<typeof queryString.stringify>[0],
        )}`;
    return [
      requestUrl,
      {
        method,
        headers,
      },
    ];
  } else {
    return [
      url,
      {
        method,
        headers,
        body: JSON.stringify(params),
      },
    ];
  }
};

const isJSON = (text: string) => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

interface ErrorResponse {
  message: string;
}

const errorResponse = new Response('', {
  status: 500,
  statusText: 'Network Error',
});

type ResponseType = 'text' | 'blob';

interface FetchOptions {
  responseType: ResponseType;
}

const defaultFetchOptions: FetchOptions = {
  responseType: 'text',
};

const useFetch = <R, E extends Response | ErrorResponse = ErrorResponse>(
  method: HTTPMethod,
  url: string,
  optionsArg: Partial<FetchOptions> = {},
): Result<R, E> => {
  const options: FetchOptions = { ...defaultFetchOptions, ...optionsArg };

  const intl = useIntl();
  const { refreshAccessToken } = useAuthentication();
  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = useState<R | null>(null);
  const [error, setError] = useState<E | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [responseHeaders, setResponseHeaders] = useState<Headers | null>(null);
  const { unauthLogout } = useError();

  const reset = useCallback(() => {
    setError(null);
    setStatus(null);
    setData(null);
    setResponseHeaders(null);
  }, [setData, setError, setStatus, setResponseHeaders]);

  const sendRequest = useCallback(
    async ({ body, headers }: SendRequestOptions = {}): Promise<
      SendRequestReturnType<R>
    > => {
      setIsFetching(true);
      reset();

      const requestHeaders = buildRequestHeaders(
        headers || {},
        accessTokenStorage.get(),
      );
      const payload = buildRequestPayload(method, url, body, requestHeaders);
      const response = await fetch(...payload).catch(() => errorResponse);

      let data: R = {} as R;
      let textBody: string | null;

      if (response.status === 401) {
        try {
          await refreshAccessToken();
          return sendRequest({ body, headers });
        } catch (e) {
          if ((e as CommonErrorResponse).status === 401) {
            unauthLogout();
          } else {
            const errorResponse: ErrorResponse = {
              message: intl.formatMessage({
                id: 'forms.errors.unexpected',
              }),
            };
            setError(errorResponse as E);
          }
        }
      } else {
        // 成功・失敗に関わらずbodyは一度だけ読む
        if (options.responseType === 'blob') {
          // blobの場合のみblobで読む（cloneを使用してbody stream already readエラーを防ぐ）
          const blobBody = (data = (await response
            .clone()
            .blob()) as unknown as R);
          setData(blobBody);
        } else {
          // textとして読む
          textBody = await response.text();

          if (!response.ok) {
            if (isJSON(textBody ?? '')) {
              const { error } = JSON.parse(textBody);
              setError((error ? error : response) as E);
            } else {
              const errorResponse: ErrorResponse = {
                message: intl.formatMessage({
                  id: 'forms.errors.unexpected',
                }),
              };
              setError(errorResponse as E);
            }
          } else {
            if (isJSON(textBody ?? '')) {
              const responseBody = (data = JSON.parse(textBody!));
              setData(responseBody as R);
            }
          }
        }
      }

      setResponseHeaders(response.headers);
      setStatus(response.status);
      setIsFetching(false);

      return {
        data,
        status: response.status,
      };
    },
    [
      reset,
      method,
      url,
      refreshAccessToken,
      unauthLogout,
      intl,
      options.responseType,
    ],
  );

  return {
    fetch: sendRequest,
    reset,
    isFetching,
    error,
    data,
    status,
    headers: responseHeaders,
  };
};

export default useFetch;
